/**
 * All Results page — Admin/HR view of all employee results
 */

import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { penilaianApi } from '../api/penilaianApi'
import {
  RESULT_STATUS_LABELS,
  RESULT_STATUS_COLORS,
} from '../types/penilaian.types'
import type { AssessmentResult, EmployeeOverviewRow } from '../types/penilaian.types'

type MergedRow = {
  employee: EmployeeOverviewRow['employee']
  assignments: EmployeeOverviewRow['assignments']
}

export default function PenilaianResultsPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('')
  const [search, setSearch] = useState('')

  const { data: periodsData } = useQuery({
    queryKey: ['penilaian-periods'],
    queryFn: () => penilaianApi.getPeriods(),
  })

  // Primary: computed results for the period
  const { data: resultsData, isLoading: resultsLoading } = useQuery({
    queryKey: ['penilaian-results', selectedPeriodId],
    queryFn: () => penilaianApi.getResults({ period_id: selectedPeriodId }),
    enabled: !!selectedPeriodId,
  })

  // Secondary: assignment status per employee (admin/HR only)
  const { data: overviewData, isLoading: overviewLoading } = useQuery({
    queryKey: ['penilaian-overview', selectedPeriodId],
    queryFn: () => penilaianApi.getEmployeeOverview(selectedPeriodId),
    enabled: !!selectedPeriodId,
    retry: false,
  })

  const computeMutation = useMutation({
    mutationFn: ({ periodId, employeeId }: { periodId: string; employeeId: string }) =>
      penilaianApi.computeScores(periodId, employeeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['penilaian-results', selectedPeriodId] })
      toast.success('Skor berhasil dihitung')
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal menghitung skor'),
  })

  const computeAllMutation = useMutation({
    mutationFn: (periodId: string) => penilaianApi.computeAllScores(periodId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['penilaian-results', selectedPeriodId] })
      qc.invalidateQueries({ queryKey: ['penilaian-overview', selectedPeriodId] })
      if (data.total > 0) {
        toast.success(`${data.total} skor berhasil dihitung`)
      } else {
        toast('Tidak ada skor yang dapat dihitung — pastikan penilai sudah mengumpulkan penilaian', {
          icon: 'ℹ️',
        })
      }
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal menghitung skor'),
  })

  const periods = periodsData?.data ?? []
  const results: AssessmentResult[] = resultsData?.data ?? []

  useEffect(() => {
    if (selectedPeriodId || periods.length === 0) return
    const open = periods.find((p) => p.status === 'open')
    if (open) { setSelectedPeriodId(open.id); return }
    const sorted = [...periods].sort((a, b) =>
      b.year !== a.year ? b.year - a.year : b.quarter - a.quarter
    )
    if (sorted[0]) setSelectedPeriodId(sorted[0].id)
  }, [periods, selectedPeriodId])

  const resultByEmployee = useMemo(() => {
    const map: Record<string, AssessmentResult> = {}
    for (const r of results) map[r.employee_id] = r
    return map
  }, [results])

  // Merge overview + results into a single list.
  // Only show employees who have at least one assignment OR a computed result for
  // this period — avoids flooding the table with 50 irrelevant rows.
  const allRows = useMemo((): MergedRow[] => {
    const seenIds = new Set<string>()
    const merged: MergedRow[] = []

    // From overview: employees with assignments this period
    for (const row of overviewData?.data ?? []) {
      if (row.assignments.length > 0 || resultByEmployee[row.employee.id]) {
        seenIds.add(row.employee.id)
        merged.push(row)
      }
    }

    // From results: employees whose result exists but weren't in overview
    // (e.g. overview unavailable, or employee deactivated after compute)
    for (const r of results) {
      if (!seenIds.has(r.employee_id)) {
        seenIds.add(r.employee_id)
        if (r.employee) {
          merged.push({
            employee: {
              id: r.employee_id,
              display_name: r.employee.display_name ?? null,
              full_name: null,
              email: r.employee.email ?? '',
              job_title: r.employee.job_title ?? null,
              department: r.employee.department ?? null,
              division_id: null,
              division_name: null,
              manager_id: null,
              manager_name: null,
            },
            assignments: [],
          })
        }
      }
    }

    // Sort: employees with results first, then by name
    merged.sort((a, b) => {
      const aHasResult = !!resultByEmployee[a.employee.id]
      const bHasResult = !!resultByEmployee[b.employee.id]
      if (aHasResult !== bHasResult) return aHasResult ? -1 : 1
      const aName = (a.employee.display_name ?? a.employee.full_name ?? '').toLowerCase()
      const bName = (b.employee.display_name ?? b.employee.full_name ?? '').toLowerCase()
      return aName.localeCompare(bName)
    })

    return merged
  }, [overviewData, results, resultByEmployee])

  const readyCount = useMemo(() =>
    allRows.filter((row) => {
      const hasSubmitted = row.assignments.some((a) => a.status === 'submitted')
      const result = resultByEmployee[row.employee.id]
      return hasSubmitted && result?.approval_status !== 'approved'
    }).length,
    [allRows, resultByEmployee]
  )

  const approvedCount = useMemo(() =>
    results.filter((r) => r.approval_status === 'approved').length, [results])

  const computedPendingCount = useMemo(() =>
    results.filter((r) => r.approval_status !== 'approved').length, [results])

  const filteredRows = useMemo(() => {
    if (!search.trim()) return allRows
    const q = search.toLowerCase()
    return allRows.filter((row) => {
      const name = (row.employee.display_name ?? row.employee.full_name ?? row.employee.email).toLowerCase()
      const dept = (row.employee.department ?? '').toLowerCase()
      return name.includes(q) || dept.includes(q)
    })
  }, [allRows, search])

  const isLoading = resultsLoading || overviewLoading

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hasil Penilaian</h1>
          <p className="text-sm text-gray-500 mt-1">Rekapitulasi nilai performansi semua pegawai</p>
        </div>
        {selectedPeriodId && overviewData && (
          <button
            onClick={() => computeAllMutation.mutate(selectedPeriodId)}
            disabled={computeAllMutation.isPending || readyCount === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {computeAllMutation.isPending ? 'Menghitung...' : (
              <>
                Hitung Semua
                {readyCount > 0 && (
                  <span className="bg-white/25 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                    {readyCount}
                  </span>
                )}
              </>
            )}
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Periode</label>
          <select
            value={selectedPeriodId}
            onChange={(e) => { setSelectedPeriodId(e.target.value); setSearch('') }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-56"
          >
            <option value="">-- Pilih periode --</option>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        {selectedPeriodId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cari</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nama atau departemen..."
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-56"
            />
          </div>
        )}
      </div>

      {selectedPeriodId && allRows.length > 0 && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Total Karyawan</p>
            <p className="text-2xl font-bold text-gray-900">{allRows.length}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Siap Dihitung</p>
            <p className="text-2xl font-bold text-blue-600">{readyCount}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Sudah Dihitung</p>
            <p className="text-2xl font-bold text-amber-600">{computedPendingCount}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Disetujui</p>
            <p className="text-2xl font-bold text-green-600">{approvedCount}</p>
          </div>
        </div>
      )}

      {!selectedPeriodId ? (
        <div className="text-center py-12 text-gray-400">Pilih periode untuk melihat data</div>
      ) : isLoading ? (
        <div className="text-center py-12 text-gray-500">Memuat...</div>
      ) : allRows.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="font-medium mb-1">Belum ada data penilaian di periode ini</p>
          <p className="text-sm mt-1">Atur penugasan penilai di halaman Penugasan, lalu tunggu penilaian dikumpulkan</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-medium text-gray-600">Pegawai</th>
                {overviewData && (
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Penilai</th>
                )}
                <th className="text-center px-4 py-3 font-medium text-gray-600">HS</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">SS</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">AKHLAK</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">Nilai Akhir</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredRows.map((row) => {
                const result = resultByEmployee[row.employee.id]
                const assignments = row.assignments
                const submittedCount = assignments.filter((a) => a.status === 'submitted').length
                const totalCount = assignments.length
                const hasSubmitted = submittedCount > 0
                const canCompute = hasSubmitted && result?.approval_status !== 'approved'

                return (
                  <tr key={row.employee.id} className="hover:bg-gray-50">
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-900">
                        {row.employee.display_name ?? row.employee.full_name ?? row.employee.email}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">{row.employee.department}</div>
                    </td>
                    {overviewData && (
                      <td className="px-4 py-4">
                        {totalCount > 0 ? (
                          <>
                            <div className="flex flex-wrap gap-1 mb-1">
                              {assignments.map((a) => (
                                <span
                                  key={a.id}
                                  title={a.assessor_name ?? undefined}
                                  className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                    a.status === 'submitted'
                                      ? 'bg-green-100 text-green-700'
                                      : a.status === 'in_progress'
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : 'bg-gray-100 text-gray-400'
                                  }`}
                                >
                                  {a.assessor_role}
                                </span>
                              ))}
                            </div>
                            <div className="text-xs text-gray-400">
                              {submittedCount}/{totalCount} dikumpulkan
                            </div>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-4 text-center text-gray-700">
                      {result?.hs_score != null ? result.hs_score.toFixed(1) : '—'}
                    </td>
                    <td className="px-4 py-4 text-center text-gray-700">
                      {result?.ss_score != null ? result.ss_score.toFixed(1) : '—'}
                    </td>
                    <td className="px-4 py-4 text-center text-gray-700">
                      {result?.akhlak_score != null ? result.akhlak_score.toFixed(1) : '—'}
                    </td>
                    <td className="px-4 py-4 text-center font-semibold text-gray-900">
                      {result?.final_score != null ? result.final_score.toFixed(1) : '—'}
                    </td>
                    <td className="px-4 py-4">
                      {result ? (
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          RESULT_STATUS_COLORS[result.approval_status] ?? 'bg-gray-100 text-gray-600'
                        }`}>
                          {RESULT_STATUS_LABELS[result.approval_status] ?? result.approval_status}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">
                          {hasSubmitted ? 'Siap dihitung' : '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        {result && (
                          <button
                            onClick={() => navigate(`/penilaian/results/${result.id}`)}
                            className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                          >
                            Lihat Detail
                          </button>
                        )}
                        {canCompute && selectedPeriodId && (
                          <button
                            onClick={() =>
                              computeMutation.mutate({
                                periodId: selectedPeriodId,
                                employeeId: row.employee.id,
                              })
                            }
                            disabled={computeMutation.isPending}
                            className="text-xs text-gray-500 hover:text-gray-700 hover:underline disabled:opacity-40 whitespace-nowrap"
                          >
                            {result ? 'Hitung Ulang' : 'Hitung'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {filteredRows.length === 0 && search && (
            <div className="text-center py-8 text-gray-400 text-sm">
              Tidak ada karyawan yang cocok dengan "{search}"
            </div>
          )}
        </div>
      )}
    </div>
  )
}
